/**
 * Subscription Error Middleware for OpenAPI Client
 * 
 * Middleware that intercepts 403 errors with subscription-related error codes
 * and redirects to the settings/plans page.
 */

import type { Middleware, ResponseContext } from '@/api/generated/client/runtime';

/**
 * Create a middleware that handles subscription errors
 * 
 * @param setLocationFn - Function to navigate to a different route
 * @returns Middleware instance
 */
export function createSubscriptionErrorMiddleware(
  setLocationFn: (path: string) => void
): Middleware {
  return {
    post: async (context: ResponseContext): Promise<Response | void> => {
      const { response } = context;
      
      // If response is 403 Forbidden, check if it's subscription-related
      if (response.status === 403) {
        try {
          // Clone the response so we can read the body without consuming it
          const clonedResponse = response.clone();
          const data = await clonedResponse.json();
          
          // Check for subscription-related error codes
          // Backend error structure: { error: { code, message, details, ... } }
          const subscriptionErrorCodes = [
            'NO_SUBSCRIPTION',
            'INACTIVE_SUBSCRIPTION'
          ];
          
          const errorCode = data?.error?.code;
          
          if (errorCode && subscriptionErrorCodes.includes(errorCode)) {
            // Redirect to settings with plans tab
            setLocationFn('/settings?tab=plans');
          }
        } catch {
          // Silently continue if response body can't be parsed
        }
      }
      
      // Return undefined to use original response
      return undefined;
    },
  };
}
